/** Browser → Vite `/api/solana` proxy → Solana JSON-RPC (avoids CORS / key in bundle). */

const RPC_PATH = "/api/solana";
const TOKEN_PROGRAM = "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA";
const TOKEN_2022_PROGRAM = "TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb";
/** Rough rent-exempt minimum for a token account (~0.00203928 SOL) */
const ATA_RENT_SOL = 0.00204;

const BASE58_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

export function isValidPubkey(s) {
  return typeof s === "string" && BASE58_RE.test(s.trim());
}

async function rpc(method, params) {
  const res = await fetch(RPC_PATH, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ jsonrpc: "2.0", id: 1, method, params }),
  });
  if (!res.ok) throw new Error(`RPC HTTP ${res.status}`);
  const body = await res.json();
  if (body.error) {
    throw new Error(body.error.message || JSON.stringify(body.error));
  }
  return body.result;
}

function parseTokenAmount(tokenAmount) {
  if (!tokenAmount) return { ui: 0, raw: "0", decimals: 0 };
  const ui =
    tokenAmount.uiAmount != null
      ? Number(tokenAmount.uiAmount)
      : Number(tokenAmount.amount) / 10 ** Number(tokenAmount.decimals || 0);
  return {
    ui: Number.isFinite(ui) ? ui : 0,
    raw: String(tokenAmount.amount || "0"),
    decimals: Number(tokenAmount.decimals || 0),
  };
}

async function tokenAccountsByOwner(owner, filter) {
  const result = await rpc("getTokenAccountsByOwner", [
    owner,
    filter,
    { encoding: "jsonParsed", commitment: "confirmed" },
  ]);
  return result?.value || [];
}

function accountsForMint(accounts, mint) {
  const matches = [];
  for (const a of accounts) {
    const info = a?.account?.data?.parsed?.info;
    if (!info || info.mint !== mint) continue;
    const amt = parseTokenAmount(info.tokenAmount);
    const ownerProgram = a?.account?.owner || null;
    matches.push({
      pubkey: a.pubkey,
      ...amt,
      state: info.state || null,
      program:
        ownerProgram === TOKEN_2022_PROGRAM
          ? "token-2022"
          : ownerProgram === TOKEN_PROGRAM
            ? "token"
            : "unknown",
    });
  }
  return matches;
}

function sumMintBalance(accounts, mint) {
  const matches = accountsForMint(accounts, mint);
  let ui = 0;
  let raw = 0n;
  let decimals = 0;
  for (const m of matches) {
    ui += m.ui;
    raw += BigInt(m.raw || "0");
    decimals = m.decimals;
  }
  return { ui, raw: raw.toString(), decimals, accounts: matches };
}

/**
 * Load SI balance, supply share, holdings, SOL, and ATA diagnostics.
 */
export async function fetchPortfolio(wallet, { indexMint, basketMints = [], basket = [] }) {
  const owner = wallet.trim();
  if (!isValidPubkey(owner)) throw new Error("Invalid Solana address");
  if (!indexMint) throw new Error("Missing index mint");

  const mintMeta = new Map();
  mintMeta.set(indexMint, { mint: indexMint, symbol: "SI", kind: "index" });
  for (const e of basket) {
    if (e?.mint) mintMeta.set(e.mint, { mint: e.mint, symbol: e.symbol || short(e.mint), kind: "basket" });
  }
  for (const m of basketMints) {
    if (m && !mintMeta.has(m)) mintMeta.set(m, { mint: m, symbol: short(m), kind: "basket" });
  }

  const [supplyRes, legacyAccounts, t22Accounts, solLamports] = await Promise.all([
    rpc("getTokenSupply", [indexMint]),
    tokenAccountsByOwner(owner, { programId: TOKEN_PROGRAM }),
    tokenAccountsByOwner(owner, { programId: TOKEN_2022_PROGRAM }).catch(() => []),
    rpc("getBalance", [owner]),
  ]);

  const allAccounts = [...legacyAccounts, ...t22Accounts];
  const supply = parseTokenAmount(supplyRes?.value);
  const indexDetail = sumMintBalance(allAccounts, indexMint);
  const sol = Number(solLamports || 0) / 1e9;

  const sharePct =
    supply.ui > 0 ? (indexDetail.ui / supply.ui) * 100 : indexDetail.ui > 0 ? null : 0;

  const holdings = {};
  const atas = [];
  let missingCount = 0;

  for (const meta of mintMeta.values()) {
    const detail = sumMintBalance(allAccounts, meta.mint);
    holdings[meta.mint] = detail;
    const open = detail.accounts.length > 0;
    if (!open) missingCount += 1;
    const primary = detail.accounts[0] || null;
    let status = "missing";
    let tip = "No token account — next airdrop may pay SOL instead until an ATA is created.";
    if (open) {
      status = detail.ui > 0 ? "funded" : "empty";
      tip =
        status === "funded"
          ? "ATA is open and holds tokens."
          : "ATA exists with zero balance — ready to receive airdrops.";
    }
    atas.push({
      mint: meta.mint,
      symbol: meta.symbol,
      kind: meta.kind,
      status,
      tip,
      balance: detail.ui,
      ata: primary?.pubkey || null,
      program: primary?.program || null,
      accountCount: detail.accounts.length,
    });
  }

  const canCoverRent = sol >= ATA_RENT_SOL;
  const ataSummary = {
    missingCount,
    openCount: atas.length - missingCount,
    sol,
    ataRentSol: ATA_RENT_SOL,
    canCoverRent,
    advice: buildAtaAdvice({ missingCount, sol, canCoverRent, indexBal: indexDetail.ui }),
  };

  return {
    wallet: owner,
    index: {
      mint: indexMint,
      ui: indexDetail.ui,
      raw: indexDetail.raw,
      decimals: indexDetail.decimals,
    },
    supply: {
      ui: supply.ui,
      raw: supply.raw,
      decimals: supply.decimals,
    },
    sharePct,
    holdings,
    atas,
    ataSummary,
  };
}

function short(m) {
  return `${m.slice(0, 4)}…`;
}

function buildAtaAdvice({ missingCount, sol, canCoverRent, indexBal }) {
  const tips = [];
  if (indexBal <= 0) {
    tips.push("This wallet holds no index tokens — it will not receive basket airdrops.");
  }
  if (missingCount > 0) {
    tips.push(
      `${missingCount} board mint(s) have no ATA. You can open them yourself now (see steps below), or wait for the worker when rent budget allows — otherwise those legs pay SOL.`,
    );
  }
  if (!canCoverRent && missingCount > 0) {
    tips.push(
      `SOL balance (${sol.toFixed(4)}) is below ~${ATA_RENT_SOL} SOL rent per new ATA. Add a little SOL first — each open account needs that deposit.`,
    );
  }
  if (missingCount === 0 && indexBal > 0) {
    tips.push("All tracked ATAs look healthy for receiving the next cycle.");
  }
  if (tips.length === 0) {
    tips.push("Lookup complete — no critical ATA issues detected.");
  }
  return tips;
}

export function fmtToken(n, digits = 4) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  if (v === 0) return "0";
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 10_000) return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (v >= 1) return v.toLocaleString(undefined, { maximumFractionDigits: digits });
  if (v >= 0.0001) return v.toFixed(6);
  return v.toExponential(2);
}

export { ATA_RENT_SOL };
